import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiHome, 
  FiPackage, 
  FiVideo, 
  FiImage, 
  FiDollarSign, 
  FiSettings, 
  FiUsers, 
  FiBarChart2, 
  FiMenu, 
  FiX, 
  FiLogOut,
  FiPlus,
  FiChevronDown,
  FiChevronRight,
  FiActivity,
  FiHelpCircle,
  FiExternalLink
} from 'react-icons/fi';

interface NavItem {
  to?: string;
  icon: JSX.Element;
  label: string;
  exact?: boolean;
  section?: string;
  items?: {
    to: string;
    icon: JSX.Element;
    label: string;
  }[];
}

interface DashboardLayoutProps {
  isAdmin?: boolean;
}

const DashboardLayout = ({ isAdmin = false }: DashboardLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if mobile on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleSection = (section: string) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  };

  // Navigation items for creator dashboard
  const creatorNavItems: NavItem[] = [
    { 
      to: '/dashboard', 
      icon: <FiHome />, 
      label: 'Dashboard',
      exact: true
    },
    { 
      section: 'content',
      icon: <FiPackage />, 
      label: 'Content',
      items: [
        { to: '/dashboard/drops', icon: <FiPackage />, label: 'My Drops' },
        { to: '/dashboard/drops/create', icon: <FiPlus />, label: 'Create Drop' },
        { to: '/dashboard/streams', icon: <FiVideo />, label: 'My Streams' },
        { to: '/dashboard/streams/create', icon: <FiPlus />, label: 'Create Stream' },
      ]
    },
    { 
      section: 'nfts',
      icon: <FiImage />, 
      label: 'NFTs',
      items: [
        { to: '/dashboard/nfts', icon: <FiImage />, label: 'My NFTs' },
        { to: '/dashboard/nfts/mint', icon: <FiPlus />, label: 'Mint NFT' },
      ]
    },
    { to: '/dashboard/wallet', icon: <FiDollarSign />, label: 'Wallet' },
    { to: '/dashboard/settings', icon: <FiSettings />, label: 'Settings' },
  ];

  // Navigation items for admin dashboard
  const adminNavItems: NavItem[] = [
    { to: '/admin', icon: <FiBarChart2 />, label: 'Overview', exact: true },
    { to: '/admin/users', icon: <FiUsers />, label: 'Users' },
    { to: '/admin/drops', icon: <FiPackage />, label: 'Drops' },
    { to: '/admin/streams', icon: <FiVideo />, label: 'Streams' },
    { to: '/admin/settings', icon: <FiSettings />, label: 'Platform Settings' },
  ];

  // Select the appropriate navigation items based on the dashboard type
  const navItems = isAdmin ? adminNavItems : creatorNavItems;

  return (
    <div className="flex h-screen bg-dark-900">
      {/* Overlay for mobile */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-dark-900/80 backdrop-blur-sm z-40"
          onClick={toggleSidebar}
        ></div>
      )}
      
      {/* Sidebar */}
      <AnimatePresence mode="wait">
        {(isSidebarOpen || !isMobile) && (
          <motion.div
            initial={isMobile ? { x: -280 } : false}
            animate={{ x: 0 }}
            exit={isMobile ? { x: -280 } : undefined}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={`fixed inset-y-0 left-0 z-50 w-72 glass-card border-r border-dark-700 ${
              isMobile ? '' : 'lg:relative'
            }`}
          >
            {/* Sidebar Header */}
            <div className="flex items-center justify-between h-16 px-6 border-b border-dark-700">
              <NavLink to="/" className="flex items-center">
                <img src="/images/logo.svg" alt="FTTF.World" className="h-8" />
              </NavLink>
              {isMobile && (
                <button
                  className="text-gray-400 hover:text-white"
                  onClick={toggleSidebar}
                  aria-label="Close sidebar"
                >
                  <FiX size={24} />
                </button>
              )}
            </div>

            {/* Sidebar Content */}
            <div className="flex flex-col justify-between h-[calc(100%-4rem)]">
              <div className="px-4 py-6 overflow-y-auto">
                {/* User Info */}
                <div className="mb-6 pb-6 border-b border-dark-700">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center text-white font-bold shadow-glow">
                      {user?.username ? user.username[0].toUpperCase() : 'U'}
                    </div>
                    <div className="ml-3">
                      <p className="text-white font-medium">
                        {user?.username || 'User'}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {isAdmin ? 'Admin' : 'Creator'}
                      </p>
                    </div>
                  </div>
                  <ConnectButton showBalance={false} chainStatus="icon" accountStatus="address" />
                </div>

                {/* Navigation */}
                <nav>
                  <ul className="space-y-1">
                    {navItems.map((item) => (
                      <li key={item.to || item.section}>
                        {item.section ? (
                          // Section with dropdown
                          <div className="mb-2">
                            <button
                              onClick={() => toggleSection(item.section!)}
                              className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-dark-700 transition-colors"
                            >
                              <div className="flex items-center">
                                <span className="mr-3 text-gray-500">{item.icon}</span>
                                <span>{item.label}</span>
                              </div>
                              <span>
                                {activeSection === item.section ? <FiChevronDown /> : <FiChevronRight />}
                              </span>
                            </button>
                            
                            <AnimatePresence>
                              {activeSection === item.section && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <ul className="pl-10 pr-4 space-y-1 mt-1">
                                    {item.items?.map((subItem) => (
                                      <li key={subItem.to}>
                                        <NavLink
                                          to={subItem.to}
                                          className={({ isActive }) =>
                                            `flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                                              isActive
                                                ? 'bg-primary-600/20 text-primary-400'
                                                : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                                            }`
                                          }
                                        >
                                          <span className="mr-3 text-sm">{subItem.icon}</span>
                                          <span className="text-sm">{subItem.label}</span>
                                        </NavLink>
                                      </li>
                                    ))}
                                  </ul>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ) : (
                          // Regular nav item
                          <NavLink
                            to={item.to!}
                            end={item.exact}
                            className={({ isActive }) =>
                              `flex items-center px-4 py-3 rounded-xl transition-colors ${
                                isActive
                                  ? 'bg-gradient-to-r from-primary-600/20 to-primary-600/10 text-white'
                                  : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                              }`
                            }
                          >
                            <span className="mr-3 text-gray-500">{item.icon}</span>
                            <span>{item.label}</span>
                          </NavLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>
                
                {/* Quick Links */}
                <div className="mt-8 pt-6 border-t border-dark-700">
                  <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Quick Links
                  </h3>
                  <ul className="space-y-1">
                    <li>
                      <a
                        href="https://docs.fttf.world"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2.5 rounded-xl text-gray-400 hover:bg-dark-700 hover:text-white transition-colors"
                      >
                        <FiHelpCircle className="mr-3 text-gray-500" />
                        <span className="text-sm">Documentation</span>
                        <FiExternalLink className="ml-auto text-gray-600" size={14} />
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://status.fttf.world"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-2.5 rounded-xl text-gray-400 hover:bg-dark-700 hover:text-white transition-colors"
                      >
                        <FiActivity className="mr-3 text-gray-500" />
                        <span className="text-sm">System Status</span>
                        <FiExternalLink className="ml-auto text-gray-600" size={14} />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Sidebar Footer */}
              <div className="px-4 py-4 border-t border-dark-700">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-3 text-gray-400 hover:text-white hover:bg-dark-700 rounded-xl transition-colors"
                >
                  <FiLogOut className="mr-3" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 glass-card border-b border-dark-700 flex items-center px-6 sticky top-0 z-30">
          <button
            className="lg:hidden text-gray-400 hover:text-white mr-4"
            onClick={toggleSidebar}
            aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            <FiMenu size={24} />
          </button>
          
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">
              {isAdmin ? 'Admin Dashboard' : 'Creator Dashboard'}
            </h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Quick Actions Button */}
            {!isAdmin && (
              <div className="relative group">
                <button className="btn-primary btn-sm">
                  <FiPlus className="mr-2" />
                  <span>Create New</span>
                </button>
                
                <div className="absolute right-0 mt-2 w-48 glass-card shadow-lg py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <NavLink to="/dashboard/drops/create" className="dropdown-item">
                    <FiPackage size={16} />
                    <span>Create Drop</span>
                  </NavLink>
                  <NavLink to="/dashboard/streams/create" className="dropdown-item">
                    <FiVideo size={16} />
                    <span>Start Stream</span>
                  </NavLink>
                  <NavLink to="/dashboard/nfts/mint" className="dropdown-item">
                    <FiImage size={16} />
                    <span>Mint NFT</span>
                  </NavLink>
                </div>
              </div>
            )}
            
            {/* Visit Site Link */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors hidden md:flex items-center"
            >
              <FiExternalLink className="mr-1" />
              <span>Visit Site</span>
            </a>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-dark-900 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;